import React, { useState, forwardRef } from "react";
import colors from "../../colors/colors";

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost" | "outline" | "icon";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  isLoading?: boolean;
  /** Use pill shape (fully rounded corners) */
  pill?: boolean;
}

const sizeStyles: Record<ButtonSize, React.CSSProperties> = {
  sm: {
    padding: "6px 14px",
    fontSize: "12px",
    borderRadius: "8px",
    gap: "5px",
  },
  md: {
    padding: "10px 18px",
    fontSize: "13px",
    borderRadius: "10px",
    gap: "6px",
  },
  lg: {
    padding: "12px 24px",
    fontSize: "14px",
    borderRadius: "12px",
    gap: "8px",
  },
};

const iconSizeStyles: Record<ButtonSize, React.CSSProperties> = {
  sm: {
    padding: "6px",
    fontSize: "12px",
    borderRadius: "8px",
    width: "28px",
    height: "28px",
  },
  md: {
    padding: "8px",
    fontSize: "13px",
    borderRadius: "10px",
    width: "34px",
    height: "34px",
  },
  lg: {
    padding: "10px",
    fontSize: "14px",
    borderRadius: "12px",
    width: "42px",
    height: "42px",
  },
};

const getVariantStyles = (
  variant: ButtonVariant,
  isHovered: boolean,
  isActive: boolean
): React.CSSProperties => {
  switch (variant) {
    case "primary":
      return {
        background: isActive
          ? colors.blue_dark
          : isHovered
            ? `linear-gradient(135deg, ${colors.blue_dark} 0%, #252b6e 100%)`
            : `linear-gradient(135deg, ${colors.blue_dark} 0%, #1f2463 100%)`,
        color: colors.white,
        border: "none",
        boxShadow: isHovered
          ? `0 4px 14px rgba(24, 29, 86, 0.35)`
          : `0 2px 8px rgba(24, 29, 86, 0.2)`,
        transform: isHovered && !isActive ? "translateY(-1px)" : "none",
      };
    case "secondary":
      return {
        background: isActive
          ? colors.gray_border
          : isHovered
            ? colors.gray_light
            : colors.gray_light,
        color: colors.primary,
        border: "none",
        boxShadow: "none",
        transform: "none",
      };
    case "danger":
      return {
        background: isActive
          ? `linear-gradient(135deg, #e63e37 0%, #d13530 100%)`
          : isHovered
            ? `linear-gradient(135deg, ${colors.red} 0%, #e63e37 100%)`
            : `linear-gradient(135deg, ${colors.red} 0%, #e63e37 100%)`,
        color: colors.white,
        border: "none",
        boxShadow: isHovered
          ? `0 4px 12px rgba(255, 83, 74, 0.35)`
          : `0 2px 6px rgba(255, 83, 74, 0.25)`,
        transform: isHovered && !isActive ? "translateY(-1px)" : "none",
      };
    case "ghost":
      return {
        background: isActive
          ? "rgba(0, 0, 0, 0.08)"
          : isHovered
            ? "rgba(0, 0, 0, 0.04)"
            : "transparent",
        color: colors.primary,
        border: "none",
        boxShadow: "none",
        transform: "none",
      };
    case "outline":
      return {
        background: isActive
          ? colors.blue_light
          : isHovered
            ? "rgba(24, 29, 86, 0.04)"
            : colors.white,
        color: colors.blue_dark,
        border: `2px solid ${colors.blue_dark}`,
        boxShadow: "none",
        transform: isHovered && !isActive ? "translateY(-1px)" : "none",
      };
    case "icon":
      return {
        background: isActive
          ? "rgba(0, 0, 0, 0.1)"
          : isHovered
            ? "rgba(0, 0, 0, 0.05)"
            : "transparent",
        color: colors.gray_medium,
        border: "none",
        boxShadow: "none",
        transform: "none",
      };
    default:
      return {};
  }
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      leftIcon,
      rightIcon,
      isLoading = false,
      pill = false,
      disabled,
      children,
      style,
      ...props
    },
    ref
  ) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isActive, setIsActive] = useState(false);

    const isDisabled = disabled || isLoading;

    const sizeStyle = variant === "icon" ? iconSizeStyles[size] : sizeStyles[size];
    const pillRadius = pill ? "200px" : sizeStyle.borderRadius;

    const baseStyles: React.CSSProperties = {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: 600,
      cursor: isDisabled ? "not-allowed" : "pointer",
      opacity: isDisabled ? 0.5 : 1,
      transition: "all 0.2s ease",
      outline: "none",
      whiteSpace: "nowrap",
      fontFamily: "inherit",
      ...sizeStyle,
      borderRadius: pillRadius,
      ...getVariantStyles(
        variant,
        !isDisabled && isHovered,
        !isDisabled && isActive
      ),
      ...style,
    };

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        style={baseStyles}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false);
          setIsActive(false);
        }}
        onMouseDown={() => setIsActive(true)}
        onMouseUp={() => setIsActive(false)}
        {...props}
      >
        {isLoading ? (
          <LoadingSpinner size={size} />
        ) : (
          <>
            {leftIcon && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {leftIcon}
              </span>
            )}
            {children}
            {rightIcon && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {rightIcon}
              </span>
            )}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = "Button";

// Loading spinner component
const LoadingSpinner: React.FC<{ size: ButtonSize }> = ({ size }) => {
  const spinnerSize = size === "sm" ? 12 : size === "md" ? 14 : 16;

  return (
    <svg
      width={spinnerSize}
      height={spinnerSize}
      viewBox="0 0 24 24"
      fill="none"
      style={{
        animation: "bc-button-spin 1s linear infinite",
      }}
    >
      <style>
        {`
          @keyframes bc-button-spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray="32"
        strokeDashoffset="12"
      />
    </svg>
  );
};

export default Button;
